import React, { useEffect, useState } from "react";
import { Form, Select, Input, Checkbox } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
import { GetAllCountryApi, GetTermsAndConditionApi } from '../../api/request/common.js';
import {
  RegisterApi,
  SendResendRegistrationOtpApi,
  VerifyRegistrationOtpApi,
} from '../../api/request/users.js';
import toast from "react-hot-toast";
import { Link } from 'react-router-dom';
import { handleApiError } from "../../utils/handleApiError.js";
import { useResponsive } from "../../hooks/useResponsive.js";
import { cleareCookies } from "../../services/Cookies.jsx";

const OTP_TIMER_SECONDS = 120;

function Register() {
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [tnc, setTnc] = useState([]);
  const [countryCodes, setCountryCodes] = useState([]);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const refCode = (searchParams?.get('ref') || "").trim();
  const roleType = (searchParams?.get('role') || 'user').trim();
  const emailValue = Form.useWatch("email", form);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const resetEmailVerification = () => {
    setIsEmailVerified(false);
    setVerifiedEmail("");
    setOtpSent(false);
    setOtp("");
    setTimer(0);
  };

  const getTermsAndCondition = async () => {
    try {
      const { data } = await GetTermsAndConditionApi();
      if (data?.success) {
        setTnc(data?.data?.[0]);
      }
    } catch (error) { }
  };

  const getContriens = async () => {
    try {
      const { data } = await GetAllCountryApi();
      if (data?.success) {
        const items = Array.isArray(data?.data) ? data.data : [];
        setCountries(items.map((item) => ({ ...item, value: item?.id, label: item?.countryName })));
        const codeMap = new Map();
        items
          .map((item) => {
            const code1Raw = String(item?.countryCode1 || '').trim();
            const code2 = String(item?.countryCode2 || '').trim();
            if (!code1Raw) return null;
            const digits = code1Raw.replace(/[^0-9+]/g, '');
            const code1 = digits.startsWith('+') ? digits : `+${digits}`;
            const label = code2 ? `${code1} (${code2})` : code1;
            return { value: code1, label };
          })
          .filter(Boolean)
          .forEach((code) => {
            if (!codeMap.has(code.value)) {
              codeMap.set(code.value, code);
            }
          });
        const codes = Array.from(codeMap.values()).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );
        setCountryCodes(codes);
      }
    } catch (error) { }
  };

  useEffect(() => {
    getContriens();
    getTermsAndCondition();
  }, []);

  useEffect(() => {
    if (!emailValue) {
      resetEmailVerification();
      return;
    }
    if (verifiedEmail && emailValue.trim().toLowerCase() !== verifiedEmail.trim().toLowerCase()) {
      resetEmailVerification();
    }
  }, [emailValue, verifiedEmail]);

  useEffect(() => {
    if (!otpSent) return undefined;
    const intervalId = setInterval(() => {
      setTimer((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [otpSent]);

  const validateEmailField = async () => {
    try {
      await form.validateFields(["email"]);
      return form.getFieldValue("email")?.trim();
    } catch (error) {
      return null;
    }
  };

  const handleSendOtp = async (type = 0) => {
    const email = await validateEmailField();
    if (!email) return;

    try {
      setIsSendingOtp(true);
      const { data } = await SendResendRegistrationOtpApi({ email, type });
      if (data?.success) {
        toast.success(data?.message || (type === 0 ? "OTP sent successfully." : "OTP resent successfully."));
        setOtpSent(true);
        setOtp("");
        setTimer(OTP_TIMER_SECONDS);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const email = await validateEmailField();
    if (!email) return;

    if (!otp || otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP.");
      return;
    }

    try {
      setIsVerifyingOtp(true);
      const { data } = await VerifyRegistrationOtpApi({ email, otp });
      if (data?.success) {
        toast.success(data?.message || "Email verified successfully.");
        setIsEmailVerified(true);
        setVerifiedEmail(email);
        setOtpSent(false);
        setOtp("");
        setTimer(0);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!isEmailVerified) {
      toast.error("Please verify your email before opening an account.");
      return;
    }

    try {
      setIsLoading(true);
      const cc = String(values?.countryCode || '').trim();
      const pn = String(values?.phoneNo || '').trim();
      const payload = {
        ...values,
        phoneNo: `${cc}${pn}`,
        domain: "upforex.live",
        role: roleType
      };
      delete payload.countryCode;
      cleareCookies();
      const { data } = await RegisterApi(payload);
      if (data?.success) {
        toast.success(data?.message);
        form.resetFields();
        resetEmailVerification();
        setSearchParams('tab=login');
      }
      setIsLoading(false);
    } catch (error) {
      handleApiError(error);
      setIsLoading(false);
    }
  };

  const filterOption = (input, option) => {
    const query = String(input || '').toLowerCase();
    const label = String(option?.label || '').toLowerCase();
    const value = String(option?.value || '').toLowerCase();
    return label.includes(query) || value.includes(query);
  };

  if (refCode) {
    form.setFieldsValue({ affiliateCode: refCode });
  }

  const renderTncLinks = () => {
    const normalizeUrl = (url) => String(url || '').replace(/[`]/g, '').trim();

    const items = Array.isArray(tnc?.termsAndConditions) ? tnc.termsAndConditions : [];
    if (items.length === 0) return null;

    return items
      .filter((item) => !!item?.fileUrl)
      .map((item, index, arr) => {
        const url = normalizeUrl(item.fileUrl);
        const label = item.label || item.fileName || 'View Document';
        return (
          <span key={item.id || index}>
            <a href={url} target="_blank" rel="noopener noreferrer" className="primary underline hover:underline duration-300 ms-1" >
              {label}
            </a>
            {index !== arr.length - 1 && ','}
          </span>
        );
      });
  };

  const isAllowKey = (e) => {
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    const value = e.target.value;
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "End", "Home", "Enter"];
    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
    if (/[0-9]/.test(e.key) && value.length >= 10) {
      e.preventDefault();
    }
  };

  const handlePhonePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData?.getData('text') || '';
    const normalized = pastedText.replace(/\D/g, '').slice(0, 10);
    form.setFieldValue('phoneNo', normalized);
  };

  const emailInputClassName = isEmailVerified
    ? "border border-[#22C55E] rounded-[14px] md:rounded-lg w-full ps-3 md:ps-2 py-3 md:py-2 placeholder:text-xs bg-[#F0FDF4]"
    : "border border-slate-200 md:border-[#d9d9d9] rounded-[14px] md:rounded-lg w-full ps-3 md:ps-2 py-3 md:py-2 placeholder:text-xs";

  return (
    <div className="auth-page register-page">
      {isMobile && <div className="my-5 mt-10">
        <p className="text-xl font-bold mb-2 text-dark-200">Register</p>
        <p className="text-gray-500">Welcome, Please register to access your Account</p>
      </div>}
      <Form autoComplete="off" form={form} onFinish={handleSubmit} className="register-form">
        <div className="grid sm:grid-cols-2 grid-cols-1 gap-x-4 gap-y-3 text-sm font-normal">
          <div className="sm:col-span-2 register-field">
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">Account Type</p>}
            <Form.Item name="accountType" className="register-form-item" rules={[{ required: true, message: 'Please select account type.' }]}>
              <Select
                placeholder={<p className="text-xs">Select account type</p>}
                dropdownAlign={{ offset: [0, 0] }}
                popupClassName="custom-select-dropdown"
                options={[
                  { value: 1, label: "Individual" },
                  { value: 2, label: "Corporate" },
                ]}
              />
            </Form.Item>
          </div>
          <div className="sm:col-span-2 register-field">
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">Title</p>}
            <Form.Item name="title" className="register-form-item" rules={[{ required: true, message: 'Please select title.' }]}>
              <Select
                placeholder={<p className="text-xs">Select title</p>}
                dropdownAlign={{ offset: [0, 0] }}
                popupClassName="custom-select-dropdown"
                options={[
                  { value: 1, label: "Mr" },
                  { value: 2, label: "Mrs" },
                  { value: 3, label: "Ms" },
                  { value: 4, label: "Dr" },
                  { value: 5, label: "Other" },
                ]}
              />
            </Form.Item>
          </div>
          <div className="register-field">
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">First Name</p>}
            <Form.Item name='firstName' className="register-form-item" rules={[{ required: true, message: 'Please enter first name.' }]}>
              <Input placeholder="First name" className="border border-slate-200 md:border-[#d9d9d9] rounded-[14px] md:rounded-lg  w-full ps-3 md:ps-2 py-3 md:py-2 placeholder:text-xs" />
            </Form.Item>
          </div>
          <div className="register-field">
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">Last Name</p>}
            <Form.Item name='lastName' className="register-form-item" rules={[{ required: true, message: 'Please enter last name.' }]}>
              <Input placeholder="Last name" className="border border-slate-200 md:border-[#d9d9d9] rounded-[14px] md:rounded-lg w-full ps-3 md:ps-2 py-3 md:py-2 placeholder:text-xs" />
            </Form.Item>
          </div>
          <div className={`sm:col-span-2 register-field ${isEmailVerified ? "mb-2" : ""}`}>
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">Email</p>}
            <Form.Item
              name='email'
              className="register-form-item !mb-0"
              rules={[{ required: true, type: 'email', message: 'Please enter valid email.' }]}
            >
              <Input
                placeholder="Email-id"
                className={emailInputClassName}
                disabled={isEmailVerified}
                suffix={
                  isEmailVerified ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#15803D]">
                      <CheckCircleFilled />
                      Verified
                    </span>
                  ) : !otpSent ? (
                    <button
                      type="button"
                      onClick={() => handleSendOtp(0)}
                      disabled={isSendingOtp}
                      className="rounded-md border border-primary px-3 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSendingOtp ? "Sending..." : "Verify"}
                    </button>
                  ) : null
                }
              />
            </Form.Item>
            {!isEmailVerified && !otpSent && (
              <p className="mt-1 mb-2 text-xs leading-4 text-[#8B8B8B]">
                Verify your email to enable account creation.
              </p>
            )}
            {otpSent && !isEmailVerified && (
              <div className="mt-2 mb-2 rounded-xl border border-[#D6E4FF] bg-[#F5F9FD] p-3">
                <p className="mb-2 text-sm font-medium text-[#585F70]">OTP</p>
                <Input.OTP
                  length={6}
                  value={otp}
                  onChange={setOtp}
                  style={{ width: "100%" }}
                />
                <div className="mt-2 flex items-center justify-center">
                  {timer === 0 ? (
                    <button
                      type="button"
                      onClick={() => handleSendOtp(1)}
                      disabled={isSendingOtp}
                      className="text-sm font-semibold text-primary disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span className="font-normal text-[#585F70]">Didn't receive it?</span> Resend OTP
                    </button>
                  ) : (
                    <p className="text-sm text-[#585F70]">Resend OTP in {formatTime(timer)}</p>
                  )}
                </div>
                <div className="mt-2">
                  <LoadableButton
                    type="button"
                    className="bg-primary text-[#FFFFFF] font-semibold text-base rounded-lg w-full py-2 text"
                    lable="Verify"
                    isLoading={isVerifyingOtp}
                    loadingLable="Verifying..."
                    disabled={otp.length !== 6}
                    onClick={handleVerifyOtp}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="sm:col-span-2 register-field">
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">Phone No.</p>}
            <div className="flex gap-2 w-full">
              <Form.Item
                name='countryCode'
                rules={[{ required: true, message: 'Please select country code.' }]}
                className="register-form-item inline-block w-1/3 !mb-0"
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  filterOption={filterOption}
                  options={countryCodes}
                  optionLabelProp="value"
                  placeholder={<p className="text-xs">+Code</p>}
                  dropdownAlign={{ offset: [0, 0] }}
                  popupClassName="custom-select-dropdown"
                />
              </Form.Item>
              <Form.Item
                name='phoneNo'
                rules={[{ required: true, message: 'Please enter phone number.' }]}
                className="register-form-item inline-block w-3/4 !mb-0"
              >
                <Input
                  maxLength={10}
                  placeholder="Phone No."
                  className="border border-slate-200 md:border-[#d9d9d9] rounded-[14px] md:rounded-lg w-full ps-3 md:ps-2 py-3 md:py-2 placeholder:text-xs"
                  onKeyDown={(e) => isAllowKey(e)}
                  onPaste={handlePhonePaste}
                />
              </Form.Item>
            </div>
          </div>
          <div className="w-full register-field">
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">Select Country</p>}
            <Form.Item name="countryId" className="register-form-item" rules={[{ required: true, message: 'Please select a country.' }]}>
              <Select showSearch filterOption={filterOption} options={countries} placeholder={<p className="text-xs">Select country</p>} dropdownAlign={{ offset: [0, 0] }} popupClassName="custom-select-dropdown" />
            </Form.Item>
          </div>
          <div className="w-full register-field">
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">Select Nationality</p>}
            <Form.Item name="nationalityId" className="register-form-item" rules={[{ required: true, message: 'Please select a nationality.' }]}>
              <Select showSearch filterOption={filterOption} options={countries} placeholder={<p className="text-xs">Select nationality</p>} dropdownAlign={{ offset: [0, 0] }} popupClassName="custom-select-dropdown" />
            </Form.Item>
          </div>
          <div className="w-full register-field">
            {!isMobile && <p className="text-[#585F70] font-semibold text-xs mb-1">Referral Code (Optional)</p>}
            <Form.Item name="affiliateCode" className="register-form-item">
              <Input placeholder="Referral code (Optional)" className="border border-slate-200 md:border-[#d9d9d9] rounded-[14px] md:rounded-lg w-full ps-3 md:ps-2 py-3 md:py-2 placeholder:text-xs" />
            </Form.Item>
          </div>
        </div>
        <div className="mt-1">
          <Form.Item name='conditions' className="register-form-item !mb-1" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')), }]} >
            <Checkbox className='text-xs'>Yes, I agreed to the Terms and conditions</Checkbox>
          </Form.Item>
        </div>
        <div className="flex flex-wrap gap-1 text-[10px] mb-1">
          {renderTncLinks()}
        </div>
        <div className="mt-1">
          <LoadableButton
            type='submit'
            className='bg-primary text-[#FFFFFF] font-semibold text-base rounded-lg w-full py-2 text'
            lable='Open Account'
            isLoading={isLoading}
            disabled={!isEmailVerified}
          />
        </div>
        <div className="text-center text-sm mt-3">
          <p>Already have an account? <Link to="/" className="primary underline">sign in</Link></p>
        </div>
      </Form>
    </div>
  );
}

export default Register;
